import { useState } from 'react';
import { enqueueSnackbar } from '../../../../components/GlobalSnackbar/snackbarStore';

type Props = {
  open: boolean;
  onClose: () => void;
  spaceSlug?: string;
  spaceId?: string;
};

export function useGoogleScriptModal(props: Props) {
  const [copied, setCopied] = useState(false);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://votre-projet.supabase.co';
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
  const webhookUrl = `${supabaseUrl}/functions/v1/email-inbound`;

  const scriptCode = `/**
 * Synchronisation automatique Gmail -> M. Eugène (100% gratuit)
 * À coller dans https://script.google.com
 */
const CONFIG = {
  WEBHOOK_URL: "${webhookUrl}",
  SUPABASE_ANON_KEY: "${supabaseAnonKey}",
  SPACE_SLUG: "${props.spaceSlug || ''}",
  SPACE_ID: "${props.spaceId || ''}",
  // Vous pouvez cibler tous les mails non lus ayant un libellé "Copro" ou simplement unread
  SEARCH_QUERY: "label:copro is:unread",
  PROCESSED_LABEL: "copro-traite"
};

function syncGmailToCopro() {
  const threads = GmailApp.search(CONFIG.SEARCH_QUERY, 0, 10);
  let processedLabel = GmailApp.getUserLabelByName(CONFIG.PROCESSED_LABEL);
  if (!processedLabel) {
    processedLabel = GmailApp.createLabel(CONFIG.PROCESSED_LABEL);
  }

  for (const thread of threads) {
    const messages = thread.getMessages();
    for (const msg of messages) {
      if (!msg.isUnread()) continue;

      const attachments = [];
      // Exclure les images de signature et éléments graphiques inline
      const msgAttachments = msg.getAttachments({ includeInlineImages: false });
      for (const att of msgAttachments) {
        attachments.push({
          filename: att.getName(),
          contentType: att.getContentType(),
          size: att.getSize(),
          base64: Utilities.base64Encode(att.getBytes())
        });
      }

      const payload = {
        spaceId: CONFIG.SPACE_ID,
        spaceSlug: CONFIG.SPACE_SLUG,
        from: msg.getFrom(),
        to: msg.getTo(),
        subject: msg.getSubject(),
        bodyText: msg.getPlainBody(),
        bodyHtml: msg.getBody(),
        receivedAt: msg.getDate().toISOString(),
        attachments: attachments
      };

      const options = {
        method: "post",
        contentType: "application/json",
        headers: {
          "apikey": CONFIG.SUPABASE_ANON_KEY,
          "Authorization": "Bearer " + CONFIG.SUPABASE_ANON_KEY
        },
        payload: JSON.stringify(payload),
        muteHttpExceptions: true
      };

      try {
        const response = UrlFetchApp.fetch(CONFIG.WEBHOOK_URL, options);
        if (response.getResponseCode() === 200 || response.getResponseCode() === 201) {
          msg.markRead();
          thread.addLabel(processedLabel);
        } else {
          Logger.log("Erreur synchro M. Eugène: " + response.getContentText());
        }
      } catch (err) {
        Logger.log("Exception synchro: " + err);
      }
    }
  }

  // Signaler à M. Eugène que la synchronisation a bien été exécutée (heartbeat)
  try {
    UrlFetchApp.fetch(CONFIG.WEBHOOK_URL, {
      method: "post",
      contentType: "application/json",
      headers: {
        "apikey": CONFIG.SUPABASE_ANON_KEY,
        "Authorization": "Bearer " + CONFIG.SUPABASE_ANON_KEY
      },
      payload: JSON.stringify({
        action: "ping",
        spaceId: CONFIG.SPACE_ID,
        spaceSlug: CONFIG.SPACE_SLUG
      }),
      muteHttpExceptions: true
    });
  } catch (pingErr) {
    Logger.log("Erreur ping M. Eugène: " + pingErr);
  }
}
`;

  function handleCopy() {
    navigator.clipboard.writeText(scriptCode);
    setCopied(true);
    enqueueSnackbar('Script copié dans le presse-papiers !', 'success');
    setTimeout(() => setCopied(false), 2500);
  }

  return {
    scriptCode,
    copied,
    handleCopy,
  };
}
